"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { EnhancedWaxCloudWalletSignup } from "@/components/EnhancedWaxCloudWalletSignup";
import ExistingWalletLinker from "@/components/ExistingWalletLinker";
import { clientLogger } from "@/lib/client-logger";
import type { WaxUserAccount } from "@/lib/wax";

type WalletMode = "choose" | "create" | "connect";

interface WalletConnectionInlineProps {
  onWalletConnected: (accountName: string) => void;
  primaryColor?: string;
  chain?: string;
}

export function WalletConnectionInline({
  onWalletConnected,
  primaryColor,
  chain = "WAX",
}: WalletConnectionInlineProps) {
  const { data: session } = useSession();
  const [mode, setMode] = useState<WalletMode>("choose");
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userEmail = session?.user?.email || "";

  const linkWalletToAccount = async (accountName: string) => {
    setIsLinking(true);
    setError(null);

    try {
      const response = await fetch("/api/user/link-wallet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountName }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to link wallet");
      }

      clientLogger.info("Wallet linked successfully inline", { accountName });
      onWalletConnected(accountName);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to link wallet";
      setError(message);
      clientLogger.error("Error linking wallet inline", { error: message });
    } finally {
      setIsLinking(false);
    }
  };

  if (isLinking) {
    return (
      <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto mb-3"></div>
          <p className="text-yellow-700">Linking your wallet...</p>
        </div>
      </div>
    );
  }

  if (mode === "create") {
    return (
      <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-6">
        <EnhancedWaxCloudWalletSignup
          userEmail={userEmail}
          onSuccess={(accountData) => {
            linkWalletToAccount(accountData.accountName);
          }}
          onError={(errMsg) => setError(errMsg)}
          onCancel={() => {
            setMode("choose");
            setError(null);
          }}
        />
        {error && (
          <p className="text-red-600 text-sm text-center mt-3">{error}</p>
        )}
      </div>
    );
  }

  if (mode === "connect") {
    return (
      <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-6">
        <ExistingWalletLinker
          onAuthSuccess={(account: WaxUserAccount) => {
            linkWalletToAccount(account.accountName);
          }}
          onAuthError={(errMsg) => setError(errMsg)}
          primaryColor={primaryColor}
        />
        {error && (
          <p className="text-red-600 text-sm text-center mt-3">{error}</p>
        )}
        <div className="text-center mt-4">
          <button
            onClick={() => {
              setMode("choose");
              setError(null);
            }}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            &larr; Back to wallet options
          </button>
        </div>
      </div>
    );
  }

  // Default: choose mode
  const buttonStyle = primaryColor
    ? { backgroundColor: primaryColor }
    : undefined;
  const buttonClassName = primaryColor
    ? "w-full px-6 py-3 text-white rounded-lg font-medium transition-colors hover:opacity-90"
    : "w-full px-6 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors";

  return (
    <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-yellow-800 mb-2">
          {chain === "WAX" ? "WAX" : chain} Wallet Required
        </h3>
        <p className="text-yellow-700 mb-6">
          Connect a wallet to claim this digital asset.
        </p>

        {error && (
          <p className="text-red-600 text-sm mb-4">{error}</p>
        )}

        <div className="space-y-3">
          <button
            onClick={() => {
              setMode("create");
              setError(null);
            }}
            className={buttonClassName}
            style={buttonStyle}
          >
            Create New Wallet
          </button>
          <button
            onClick={() => {
              setMode("connect");
              setError(null);
            }}
            className="w-full px-6 py-3 bg-white text-yellow-700 border-2 border-yellow-400 rounded-lg font-medium hover:bg-yellow-50 transition-colors"
          >
            Connect Existing Wallet
          </button>
        </div>
      </div>
    </div>
  );
}
